"use client";

import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  fetchPrewarmImageUrls,
  warmBrowserImageCache,
} from "@/lib/welcomeEventsWarm";

/* ------------------------------------------------------------------ */
/*  Title rotation config                                              */
/* ------------------------------------------------------------------ */
const TITLE_INTERVAL_MS = 4500;
const GRUV_HOLD_MS = 20000;

/**
 * Schedules work during idle time so prewarm does not compete with welcome animation paint.
 */
const scheduleIdle = (fn: () => void): (() => void) => {
  if (typeof requestIdleCallback !== "undefined") {
    const id = requestIdleCallback(fn, { timeout: 3000 });
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(fn, 1);
  return () => clearTimeout(id);
};

/* ------------------------------------------------------------------ */
/*  Component: Welcome content overlay (titles, subtitle, CTA)         */
/*  Particles are rendered separately via ParticleCanvas in GruvShell  */
/* ------------------------------------------------------------------ */
export function WelcomeHero() {
  const t = useTranslations("welcome");
  const router = useRouter();
  const titlesRaw = t.raw("titles") as string[];
  const [titleIndex, setTitleIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const serverWarmStarted = useRef(false);
  const intentWarmStarted = useRef(false);
  const imageUrlsRef = useRef<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const isLastTitle = titleIndex === titlesRaw.length - 1;
    const holdTime = isLastTitle ? GRUV_HOLD_MS : TITLE_INTERVAL_MS;

    const timeout = setTimeout(() => {
      setIsTyping(false);
      setTimeout(() => {
        setTitleIndex((prev) => (prev + 1) % titlesRaw.length);
        setIsTyping(true);
      }, 600);
    }, holdTime);

    return () => clearTimeout(timeout);
  }, [titleIndex, titlesRaw.length]);

  const warmOnIntent = useCallback(() => {
    if (intentWarmStarted.current) {
      return;
    }
    intentWarmStarted.current = true;

    router.prefetch("/events");
    void warmBrowserImageCache(imageUrlsRef.current, abortRef.current?.signal);
  }, [router]);

  /* Server-side landing cache only on mount; route/image warm on CTA intent. */
  useEffect(() => {
    if (serverWarmStarted.current) {
      return;
    }
    serverWarmStarted.current = true;

    abortRef.current = new AbortController();

    const cancelIdle = scheduleIdle(() => {
      void fetchPrewarmImageUrls().then((urls) => {
        imageUrlsRef.current = urls;
      });
    });

    return () => {
      cancelIdle();
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const handleCTAClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      warmOnIntent();
      setIsExiting(true);
      setTimeout(() => {
        router.push("/events");
      }, 700);
    },
    [router, warmOnIntent],
  );

  const prefix = t("prefix");
  const currentTitle = titlesRaw[titleIndex];
  const isGruv = titleIndex === titlesRaw.length - 1;

  return (
    <section className={`welcome-hero ${isExiting ? "welcome-hero--exiting" : ""}`}>
      <div className="welcome-hero__content">
        <h1 className="welcome-hero__typewriter">
          <span className="welcome-hero__prefix">{prefix}</span>
          <span
            key={titleIndex}
            className={`welcome-hero__dynamic ${
              isTyping
                ? isGruv
                  ? "welcome-hero__dynamic--gruv"
                  : "welcome-hero__dynamic--typing"
                : "welcome-hero__dynamic--exit"
            }`}
            style={{ "--n": currentTitle.length } as React.CSSProperties}
          >
            {currentTitle}
          </span>
        </h1>
        <p className="welcome-hero__subtitle">{t("subtitle")}</p>
        <Link
          href="/events"
          className="welcome-hero__cta"
          onClick={handleCTAClick}
          onPointerEnter={warmOnIntent}
        >
          {t("cta")}
        </Link>
      </div>
    </section>
  );
}
